/**
 * Which kind of value is this — the workspace's official value, or a value
 * that belongs to one session?
 *
 * Canon (Doc 03 §2.7, glossary "Value Collection" / "Value Source Selection"):
 * values are proposed, then accepted. An accepted value with no session scope is
 * the workspace's value — the one a Track snapshots at lock time when no Value
 * Collection is selected. An accepted value with a session scope belongs to that
 * session only. A proposal (value_proposals row) is a contribution: someone's
 * input that has not been accepted yet.
 *
 * The product word for the workspace value is "official". A session's value is
 * "this session's value" — never a stand-in for the official one. Every
 * "does this item have a value?" question outside a session asks about the
 * official value only; inside a session it asks about the official value or
 * that session's own value — never another session's.
 */

export interface ScopedValue {
  sessionScope?: string | null;
}

export type ValueScopeKind = 'official' | 'this_session' | 'other_session';

/** True for the workspace's official value (accepted, no session scope). */
export function isOfficialValue(value: ScopedValue): boolean {
  return !value.sessionScope;
}

/**
 * Does this accepted value count in the given context?
 * - No session: only the official value counts.
 * - In a session: the official value or that session's own value.
 * Another session's value never counts.
 */
export function valueCountsFor(value: ScopedValue, sessionId?: string | null): boolean {
  if (isOfficialValue(value)) return true;
  return !!sessionId && value.sessionScope === sessionId;
}

export function valueScopeKind(value: ScopedValue, sessionId?: string | null): ValueScopeKind {
  if (isOfficialValue(value)) return 'official';
  return sessionId && value.sessionScope === sessionId ? 'this_session' : 'other_session';
}

/** The words a person sees for each kind. One word, one meaning. */
export const VALUE_SCOPE_LABEL: Record<ValueScopeKind, string> = {
  official: 'Official',
  this_session: 'This session only',
  other_session: 'Another session',
};

/** Keep only the values that count in this context (see valueCountsFor). */
export function valuesThatCount<T extends ScopedValue>(values: readonly T[], sessionId?: string | null): T[] {
  return values.filter((v) => valueCountsFor(v, sessionId));
}
